import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

// Convert base64 URL to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Check if already subscribed (with timeout to avoid hanging)
      try {
        // Only check subscription if there's an existing service worker registration
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
    
    setIsLoading(false);
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        if (permissionResult === 'denied') {
          toast.error('Notifications blocked. Please enable in browser settings: Click the lock icon in the address bar → Site settings → Notifications → Allow');
        } else {
          toast.error('Notification permission was not granted');
        }
        setIsLoading(false);
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const keyResponse = await axios.get(`${API}/push/vapid-public-key?t=${Date.now()}`);
      const vapidPublicKey = keyResponse.data?.publicKey;

      console.log('VAPID key response:', keyResponse.data);

      if (!vapidPublicKey || vapidPublicKey === '') {
        toast.error('Push notifications not configured on server. Please add VAPID keys in Settings.');
        setIsLoading(false);
        return false;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Failed to enable push notifications');
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();

        // Notify server
        await axios.delete(`${API}/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint }
        });
      }

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to disable push notifications');
    }

    setIsLoading(false);
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe
  };
}

export function PushNotificationButton({ className = '' }) {
  const { isSupported, isSubscribed, isLoading, permission, subscribe, unsubscribe } = usePushNotifications();

  if (!isSupported) {
    return null;
  }

  const handleClick = () => {
    if (isSubscribed) {
      unsubscribe();
    } else {
      subscribe();
    }
  };

  return (
    <Button
      variant={isSubscribed ? "default" : "outline"}
      size="sm"
      onClick={handleClick}
      disabled={isLoading || permission === 'denied'}
      className={className}
      data-testid="push-notification-btn"
      title={
        permission === 'denied' 
          ? 'Notifications blocked - enable in browser settings' 
          : isSubscribed 
            ? 'Click to disable notifications' 
            : 'Click to enable notifications'
      }
    >
      {isLoading ? (
        <span className="animate-spin">...</span>
      ) : isSubscribed ? (
        <>
          <BellRing size={16} className="mr-2" />
          Notifications On
        </>
      ) : (
        <>
          {permission === 'denied' ? <BellOff size={16} className="mr-2" /> : <Bell size={16} className="mr-2" />}
          {permission === 'denied' ? 'Blocked' : 'Enable Notifications'}
        </>
      )}
    </Button>
  );
}

export default PushNotificationButton;
